import { ProductGroupFilter, SideFilters, ToggleSwitch } from '@retail/components';
import {
  GroupOption,
  IncludesOrMissing,
  ProductGroupCount,
  ProductGroup,
} from '@retail/products/types';
import { SupplierBase } from '@retail/suppliers/types';
import { SelectSupplier } from '@retail/suppliers/components';
import { MgSupplierItemSearchField } from '../MgItem';
import { ClimateDataValueFilter } from './ClimateDataValueFilter';
import { useProductsTFunction } from '@retail/products/i18n';
import { SupplierItemInsightValueFilter } from './SupplierItemInsightValueFilter';
import { useDisclosure } from '@shared/hooks';
import { IncludesOrMissingFieldToggleGroup } from './IncludesOrMissingFieldToggleGroup';
import {
  DocumentationFilterDataKey,
  EnvironmentDataFilterKey,
  insightFilters,
  InsightFilters,
  PictureFilterDataKey,
} from '@retail/products/utils';
import { AssortmentTag } from '@retail/my-assortment/types';
import { SelectAssortmentTags } from './SelectAssortmentTags';
import { useFilters } from '@retail/products/hooks';

interface Props {
  showFilters: boolean;
  assortmentTags?: AssortmentTag[];
  suppliers: SupplierBase[];
  productGroupHierarchy: ProductGroupCount[];
  selectedSupplier: SupplierBase | null;
  selectedProductGroup: ProductGroup | null;
  subAssortmentIds: string[];
  searchQuery: string | null;
  inAssortment: GroupOption;
  includeNotIncludedItemData: boolean;
  environmentValuesSelected: EnvironmentDataFilterKey[];
  documentationValuesSelected: DocumentationFilterDataKey[];
  pictureValuesSelected: PictureFilterDataKey[];
  climateDataA1ToA3Included: boolean;
  includedOrMissingFieldValue: IncludesOrMissing;

  toggleIncludeNotIncludedItemData: (include: boolean) => void;
  updateEnvironmentFilterValues: (value: EnvironmentDataFilterKey) => void;
  updateDocumentationFilterValues: (value: DocumentationFilterDataKey) => void;
  updatePictureFilterValues: (value: PictureFilterDataKey) => void;
  toggleClimateDataA1ToA3Included: () => void;
  toggleIncludedOrMissingFieldValue: (includeOrMissing: IncludesOrMissing) => void;
}

export function InsightReportFilters({
  showFilters,
  assortmentTags,
  suppliers,
  productGroupHierarchy,
  searchQuery,
  subAssortmentIds,
  selectedSupplier,
  selectedProductGroup,
  includeNotIncludedItemData,
  environmentValuesSelected,
  climateDataA1ToA3Included,
  includedOrMissingFieldValue,
  inAssortment,
  toggleIncludeNotIncludedItemData,
  updateEnvironmentFilterValues,
  toggleClimateDataA1ToA3Included,
  toggleIncludedOrMissingFieldValue,
}: Props) {
  const t = useProductsTFunction();
  const { setFilters, updateFilter } = useFilters<InsightFilters>(insightFilters);

  const { isOpen: showProductGroups, onToggle: toggleProductGroups } = useDisclosure(false);

  return (
    <SideFilters showFilters={showFilters}>
      <ToggleSwitch
        justifyContent="space-between"
        toggle={includeNotIncludedItemData}
        setToggle={toggleIncludeNotIncludedItemData}
        text={t('products.mgItem.showUnincludedData')}
      />
      <ToggleSwitch
        justifyContent="space-between"
        toggle={inAssortment === 'YES'}
        setToggle={() => setFilters({ inAssortment: inAssortment === 'YES' ? undefined : 'YES' })}
        text={t('products.mgItem.supplierItem.inAssortment')}
      />

      {assortmentTags && (
        <SelectAssortmentTags
          assortmentTags={assortmentTags}
          selectedAssortmentTags={subAssortmentIds}
          selectAssortmentTags={(subAssortmentId: string) =>
            updateFilter('subAssortmentIds', subAssortmentId)
          }
        />
      )}

      <SelectSupplier
        suppliers={suppliers}
        selectedSupplier={selectedSupplier?.supplierNumber ?? null}
        getSupplierId={(supplier: SupplierBase) => supplier.supplierNumber}
        getSupplierName={(supplier: SupplierBase) => supplier.name}
        selectSupplier={(supplierNumber) =>
          setFilters({ supplierNumber: supplierNumber?.toString() })
        }
      />
      <MgSupplierItemSearchField
        searchTerm={searchQuery ?? undefined}
        isDisabled={false}
        size="small"
        onSubmitSearch={(query: string) => setFilters({ searchQuery: query })}
      />

      <ProductGroupFilter
        productGroups={productGroupHierarchy}
        selectedProductGroup={selectedProductGroup}
        showProductGroups={showProductGroups}
        toggleShowProductGroups={toggleProductGroups}
        selectProductGroup={(groupNumber: string | null) =>
          setFilters({ productGroupNumber: groupNumber ?? undefined })
        }
        getCount={(productGroup: ProductGroup) => productGroup.count}
      />

      <IncludesOrMissingFieldToggleGroup
        includeOrMissingFieldValue={includedOrMissingFieldValue}
        switchIncludeOrMissingFieldValue={toggleIncludedOrMissingFieldValue}
      />

      <ClimateDataValueFilter
        climateDataA1ToA3Included={climateDataA1ToA3Included}
        toggleClimateDataA1ToA3Included={toggleClimateDataA1ToA3Included}
      />

      <SupplierItemInsightValueFilter
        label={t('products.mgItem.environmentDocumentation.title')}
        insightArea="environment"
        valuesSelected={environmentValuesSelected}
        updateFilterValues={(field: string) =>
          updateEnvironmentFilterValues(field as EnvironmentDataFilterKey)
        }
      />

      {/*
       TODO: Add back when filter is ready in backend
     <SupplierItemInsightValueFilter
        label={t('products.mgItem.documentation.title')}
        insightArea="documentation"
        valuesSelected={documentationValuesSelected}
        updateFilterValues={updateDocumentationFilterValues}
      />

      <SupplierItemInsightValueFilter
        label={t('products.mgItem.pictureData.pictures')}
        insightArea="picture"
        valuesSelected={pictureValuesSelected}
        updateFilterValues={updatePictureFilterValues}
      />*/}
    </SideFilters>
  );
}
